import React, { ChangeEvent, useState } from "react";
import { useSelector } from "react-redux";
import { IconButton } from "@mui/material";
import classes from "./OccupancyRateBarLineCharts.module.scss";
import {
  getCurrentYearString,
  getPreviousYearString,
} from "../../../utils/commonUtils";
import { yearsList } from "../../../utils/constants/periodLists";
import { PeriodDropdown } from "../PeriodDropdown/PeriodDropdown";
import { OccupancyRateBar } from "./components/OccupancyRateBar/OccupancyRateBar";
import { clsx } from "clsx";
import { BarIcon } from "../../../assets/icons/BarIcon";
import { LineIcon } from "../../../assets/icons/LineIcon";
import { isMobileModeSelector } from "../../../redux/selectors/appStatusSelector";
import { Typography } from "../../../shared/ui/typography";
import { Switch } from "../../../shared/ui/switch";
import { getYearlyRevenueByMonthData } from "@/redux/selectors/revenueSelector";
// import { getYearlyMarketOccupancyRateByMonth } from "@/utils/mapDataForRevenueMetrics";
import { getYearlyMarketOccupancyRateByMonthNew } from "@/utils/mapDataForRevenueMetrics";


type IOccupancyRateChartType = "bar" | "line";

export const OccupancyRateChartTypeSwitch = ({
  isChatTypeBar,
  onClick,
}: {
  isChatTypeBar: boolean;
  onClick: (type: IOccupancyRateChartType) => VoidFunction;
}) => {
  return (
    <div className={classes.switcher}>
      <IconButton
        classes={{ root: classes.button }}
        className={isChatTypeBar ? classes.activeButton : ""}
        color={isChatTypeBar ? "secondary" : "inherit"}
        aria-label="change chart type"
        onClick={onClick("bar")}
      >
        <BarIcon />
      </IconButton>
      <IconButton
        classes={{ root: classes.button }}
        className={!isChatTypeBar ? classes.activeButton : ""}
        color={!isChatTypeBar ? "secondary" : "inherit"}
        aria-label="change chart type"
        onClick={onClick("line")}
      >
        <LineIcon />
      </IconButton>
    </div>
  );
};

export const OccupancyPeriodDropdowns = ({
  fromYear,
  years,
  setFromYear,
  compareMode,
  toYear,
  setToYear,
  Switcher,
}: {
  fromYear: string;
  years: { id: string; label: string }[];
  compareMode: boolean;
  toYear: string;
  setFromYear: React.Dispatch<React.SetStateAction<string>>;
  setToYear: React.Dispatch<React.SetStateAction<string>>;
  Switcher?: React.ReactNode;
}) => {
  return (
    <div className={classes.periodBlock}>
      <PeriodDropdown
        className={classes.standardDropdown}
        period={toYear}
        periodList={years}
        setPeriod={setToYear}
        variant="standard"
      />
      {compareMode && (
        <>
          <Typography variant="text-md" weight="medium" className={classes.vs}>
            vs.
          </Typography>
          <PeriodDropdown
            className={classes.standardDropdown}
            period={fromYear}
            periodList={years}
            setPeriod={setFromYear}
            variant="standard"
          />
        </>
      )}
      {Switcher}
    </div>
  );
};

export const OccupancyRateBarLineCharts = (props: {
  bothCharts: boolean;
  page: "performance" | "occupancy";
}) => {
  const [compareMode, setCompareMode] = useState(false);
  const { bothCharts, page } = props;
  const [fromYear, setFromYear] = useState(getPreviousYearString());
  const [toYear, setToYear] = useState(getCurrentYearString());
  const [chartType, setChartType] = useState<IOccupancyRateChartType>("bar");
  const [marketEnabled, setMarketEnabled] = useState(true);
  const [marketLastYearEnabled, setMarketLastYearEnabled] = useState(false);

  const isMobileView = useSelector(isMobileModeSelector);
  const isBarActive = chartType === "bar";

  const yearlyRevenueByMonthData = useSelector(getYearlyRevenueByMonthData);

  const years = yearsList();

  const handleCompareModeChange = (value: boolean) => {
    if (value) setMarketLastYearEnabled(false);

    setMarketEnabled(!value);
    setChartType((prev) => (value ? prev : "bar"));
    setCompareMode(value);
  };

  const onMarketEnabledToggle = () => {
    setMarketEnabled((prev) => !prev);
  };
  const onMarketLastYearEnabledToggle = () => {
    setMarketLastYearEnabled(!marketLastYearEnabled);
  };

  const onChartTypeClick = (type: IOccupancyRateChartType) => () => {
    setChartType(type);
  };

  // const displayData =
  //   yearlyRevenueByMonthData &&
  //   getYearlyMarketOccupancyRateByMonth({
  //     data: yearlyRevenueByMonthData,
  //     yearFrom: compareMode ? fromYear : getPreviousYearString(toYear),
  //     yearTo: toYear,
  //   });

  const displayData =
  yearlyRevenueByMonthData &&
  getYearlyMarketOccupancyRateByMonthNew({
    data: yearlyRevenueByMonthData,
    yearFrom: compareMode ? fromYear : getPreviousYearString(toYear),
    yearTo: toYear,    
  }); 

  if (!displayData || Object.keys(displayData).length === 0) {
    return (
      <div
        className={clsx(
          classes.occupancyChartsContainer,
          classes.errorMessageContainer
        )}
      >
        <Typography
          variant="text-lg"
          weight="medium"
          className={classes.noDataMessage}
        >
          No data available for the selected period.
        </Typography>
      </div>
    );
  }

  return (
    <div
      className={
        bothCharts
          ? classes.occupancyChartsContainer
          : `${classes.occupancyChartsContainer} ${classes.occupancyBarContainer}`
      }
    >
      <div className={classes.occupancyChartsHeader}>
        <Typography variant={isMobileView ? "text-md" : "text-xl"}>
          Occupancy Rate
        </Typography>
        {isMobileView && compareMode && (
          <OccupancyRateChartTypeSwitch
            isChatTypeBar={isBarActive}
            onClick={onChartTypeClick}
          />
        )}
        {isMobileView && (
          <Switch
            id="compare-mode"
            checked={compareMode}
            onChange={({
              target: { checked },
            }: ChangeEvent<HTMLInputElement>) => {
              handleCompareModeChange(checked);
            }}
            label="Compare"
          />
        )}
        {!isMobileView && (
          <OccupancyPeriodDropdowns
            fromYear={fromYear}
            toYear={toYear}
            years={years}
            compareMode={compareMode}
            setFromYear={setFromYear}
            setToYear={setToYear}
            Switcher={
              <Switch
                id="compare-mode"
                checked={compareMode}
                onChange={({
                  target: { checked },
                }: ChangeEvent<HTMLInputElement>) => {
                  handleCompareModeChange(checked);
                }}
                label="Compare"
              />
            }
          />
        )}
      </div>
      {isMobileView && bothCharts && (
        <OccupancyPeriodDropdowns
          fromYear={fromYear}
          years={years}
          compareMode={compareMode}
          toYear={toYear}
          setFromYear={setFromYear}
          setToYear={setToYear}
        />
      )}
      <OccupancyRateBar
        data={displayData}
        fromYear={fromYear}
        toYear={toYear}
        page={page}
        compareMode={compareMode}
        marketEnabled={marketEnabled}
        marketLastYearEnabled={marketLastYearEnabled}
        onMarketEnabledToggle={() => setMarketEnabled(!marketEnabled)}
        onMarketLastYearEnabledToggle={() => setMarketLastYearEnabled(!marketLastYearEnabled) }
        chartType={chartType}
      />
      <div className={classes.legend}>
        <div className={classes.legendGroup}>
          <div className={classes.legendLabel}>
            <div className={clsx(classes.legendItem, classes.legendItemBorder)}>
              <div className={clsx(classes.brand500, classes.circle)}></div>
              <Typography variant="text-sm" className={classes["gray-600"]}>
                {toYear}
              </Typography>
            </div>
            {compareMode && (
              <div
                className={clsx(classes.legendItem, classes.legendItemBorder)}
              >
                <div className={clsx(classes.brand300, classes.circle)}></div>
                <Typography variant="text-sm" className={classes["gray-600"]}>
                  {fromYear}
                </Typography>
              </div>
            )}
          </div>
          <div className={clsx(classes.legendItem, classes.legendItemBorder)}>
            <Switch
              id="market"
              checked={marketEnabled}
              onChange={onMarketEnabledToggle}
              label={
                <div className={classes.labelItem}>
                  <div className={classes.marketLine}>
                    <div className={classes.marketLineItem}></div>
                  </div>
                  <Typography variant="text-sm" className={classes["gray-600"]}>
                    Market {compareMode && toYear}
                  </Typography>
                </div>
              }
            />
          </div>
          <div className={clsx(classes.legendItem, classes.legendItemBorder)}>
            <Switch
              id="market-last-year"
              checked={marketLastYearEnabled}
              onChange={onMarketLastYearEnabledToggle}
              label={
                <div className={classes.labelItem}>
                  <div className={classes.marketLine}>
                    <div className={classes.marketLastYearLineItem}></div>
                  </div>
                  <Typography variant="text-sm" className={classes["gray-600"]}>
                    Market {compareMode ? fromYear : " Last Year"}
                  </Typography>
                </div>
              }
            />
          </div>
        </div>
        {!isMobileView ? (
          <div className={classes.legendGroup}>
            <div className={classes.legendItem}>
              <div className={classes.legendRow}>
                {compareMode && (
                  <OccupancyRateChartTypeSwitch
                    isChatTypeBar={isBarActive}
                    onClick={onChartTypeClick}
                  />
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
